import { makeAutoObservable } from "mobx";
import {
  loadCatalog,
  loadCatalogSchemes,
} from "src/business/catalog/apiCatalog";
import { CatalogsTreeStore } from "src/business/catalog/CatalogsTree/CatalogsTreeStore";
import { ZCatalog } from "src/business/catalog/ZCatalog";
import { createRecordColumns } from "src/business/records/createRecordColumns";
import { recordsLoader } from "src/business/records/recordsLoader";
import { CatalogRecordFilters } from "src/business/records/ZCatalogRecord";
import { ZCatalogRecordSafe } from "src/business/records/ZCatalogRecordSafe";
import { ZSpecificationTyped } from "src/business/specification";
import {
  FnRowEvaluate,
  makeRowEvaluate,
} from "src/business/specification/rowEvaluate";
import { onError } from "src/common/onError";
import { optionsMaker } from "src/common/optionsMaker";
import { RemoteData } from "src/common/RemoteData";
import { RtColumn } from "src/components/ReactiveTable/RtColumn";
import { RtStore } from "src/components/ReactiveTable/RtStore";
import { ZSidLabel } from "src/types";

export type DataAttrRecordSelect = {
  catalog: ZCatalog;
  schemes: ZSpecificationTyped[];
  table: RtStore<ZCatalogRecordSafe, CatalogRecordFilters>;
  columns: RtColumn<ZCatalogRecordSafe>[];
  tree: CatalogsTreeStore;
};

export class AttrRecordSelectStore {
  constructor(
    readonly catalogId: string,
    readonly srcValue: ZSidLabel | ZSidLabel[] | null,
    readonly multi: boolean,
    readonly labelTemplate: string | null | undefined,
  ) {
    makeAutoObservable(this);
    this.init().catch(onError);
  }

  data: RemoteData<DataAttrRecordSelect> = { status: "none" };

  setData(newData: RemoteData<DataAttrRecordSelect>) {
    this.data = newData;
  }

  get srcValuesList(): ZSidLabel[] {
    const { srcValue } = this;
    if (!srcValue) return [];
    return Array.isArray(srcValue) ? srcValue : [srcValue];
  }

  async init() {
    try {
      this.setData({ status: "wait" });
      const [catalog, schemes] = await Promise.all([
        loadCatalog(this.catalogId),
        loadCatalogSchemes(this.catalogId),
      ]);
      const table = new RtStore<ZCatalogRecordSafe, CatalogRecordFilters>({
        rowKey: "id",
        loader: recordsLoader(this.catalogId, schemes),
        keepSelected: true,
        selectionType: this.multi ? "checkbox" : "radio",
      });
      table.setSelectedKeys(this.srcValuesList.map(({ id }) => id));
      const columns = createRecordColumns(
        schemes,
        catalog.displayAttribute,
        catalog.classifiers,
      );
      const tree = new CatalogsTreeStore(catalog);
      const result = { catalog, schemes, table, columns, tree };
      this.setData({ status: "ready", result });
    } catch (error) {
      this.setData({ status: "error", error });
    }
  }

  get title(): string {
    const { data } = this;
    return data.status === "ready" ? data.result.catalog.name : "";
  }

  get okDisabled() {
    return this.data.status !== "ready";
  }

  get makeOption() {
    return optionsMaker(
      this.srcValuesList,
      (row: ZCatalogRecordSafe) => this.rowEval?.(row) ?? row.name,
    );
  }

  get rowEval(): FnRowEvaluate<ZCatalogRecordSafe> | undefined {
    const { labelTemplate, data } = this;
    if (!labelTemplate || data.status !== "ready") return undefined;
    return makeRowEvaluate(labelTemplate, data.result.schemes);
  }

  get curValue(): ZSidLabel | ZSidLabel[] | null {
    const { data } = this;
    if (data.status !== "ready") return null;
    const { selectedKeys } = data.result.table;
    const list: ZSidLabel[] = selectedKeys
      .map((key) => this.makeOption(key))
      .map(({ value, label }) => ({ id: value, label }));
    return this.multi ? list : (list[0] ?? null);
  }
}
